import { AbstractControl, ValidationErrors } from '@angular/forms';
import { Observable, of } from 'rxjs';
import { map, catchError, debounceTime, switchMap, first } from 'rxjs/operators';
import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';

@Injectable({ providedIn: 'root' })
export class EmailValidatorService {
  constructor(private http: HttpClient) {}

  checkEmailUnique() {
    return (control: AbstractControl): Observable<ValidationErrors | null> => {
      const value = (control.value || '').trim().toLowerCase();

      if (!this.isValidEmail(value)) return of(null);

      return of(value).pipe(
        debounceTime(400),
        switchMap(email =>
          this.http.get<{ exists: boolean; match?: string }>(
            `/validate/account?identifier=${encodeURIComponent(email)}`
          )
        ),
        map(response => (response.exists ? { emailTaken: true } : null)),
        catchError(() => of(null)),
        first()
      );
    };
  }

  /**
   * Validates whether the provided email is in a valid format.
   *
   * Rules:
   * - Local part allows standard RFC 5322 characters (letters, numbers, special symbols)
   * - Domain part must contain at least one dot (e.g., 'example.com')
   * - Top-level domain must be at least 2 characters long
   * - Rejects malformed emails like 'user@localhost', 'user@d', or trailing dots
   *
   * @param email - The email address to validate
   * @returns true if the email is valid, false otherwise
   */
  private isValidEmail(email: string): boolean {
    const pattern =
      /^[a-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-z0-9!#$%&'*+/=?^_`{|}~-]+)*@(?:[a-z0-9-]+\.)+[a-z]{2,}$/i;
    return pattern.test(email);
  }
}
